import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import SchemaForm from 'app/components/general/SchemaForm';

import * as usersActions from 'app/ducks/users/actions';
import * as bankAccountActions from 'app/ducks/bankAccounts/actions';

import * as usersSelectors from 'app/ducks/users/selectors';
import * as bankAccountsSelectors from 'app/ducks/bankAccounts/selectors';

import SectionGroup from 'app/components/general/SectionGroup';
import HeaderThin from 'app/components/layout/HeaderThin';
import { Link } from 'react-router-dom';

import Isvg from 'react-inlinesvg';

import ArrowRightIcon from 'app/images/icons/arrow-right.svg';

const formSchema = {
  type: 'object',
  required: ['bankName', 'routingNumber', 'accountNumber', 'accountHolderName'],
  properties: {
    bankName: {
      type: 'string',
      title: 'Bank Name',
    },
    accountHolderName: {
      type: 'string',
      title: 'Account Holder Name',
    },
    routingNumber: {
      type: 'string',
      title: 'BSB',
      minLength: 4,
      inputType: 'number',
    },
    accountNumber: {
      type: 'string',
      title: 'Account Number',
      minLength: 4,
      inputType: 'number',
    },
  },
};

// const uiSchema = {
//   accountNumber: {
//     'ui:options': {
//       inputType: 'tel',
//     },
//   },
//   routingNumber: {
//     'ui:options': {
//       inputType: 'tel',
//     },
//   },
// };

const selector = (state): any => ({
  currentUser: usersSelectors.getCurrentUser(state),
  currentBankAccount: bankAccountsSelectors.getCurrentFormBankAccount(state),
  bankAccounts: bankAccountsSelectors.getBankAccounts(state),
  isLoading: state.bankAccounts.isLoading,
  isSaving: state.bankAccounts.isSaving,
  hasSaved: state.bankAccounts.hasSaved,
  hasFailed: state.bankAccounts.hasFailed,
  errors: { ...state.bankAccounts.backendErrors },
});

const actionCreators = _.merge(usersActions, bankAccountActions);

interface IBankPayoutsProps {
  actions?: any;
  bankAccounts?: any;
  currentBankAccount?: any;
  currentUser?: any;
  currentUserId?: boolean;
  errors?: any;
  isLoading?: boolean;
  isSaving?: boolean;
  hasSaved?: boolean;
  hasFailed?: boolean;
}

export interface IBankPayoutsState {
  formData: any;
  originalFormData: any;
  changed: boolean;
  saved: boolean;
  errors: any;
}

@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class BankPayouts extends React.Component<
IBankPayoutsProps,
IBankPayoutsState
> {
  static defaultProps: IBankPayoutsProps = {
    actions: undefined,
    bankAccounts: {},
    currentBankAccount: {},
    currentUser: {
      bankAccount: {},
    },
    errors: undefined,
    isLoading: false,
    isSaving: false,
    hasSaved: false,
    hasFailed: false,
  };

  state: IBankPayoutsState = {
    changed: false,
    saved: false,
    formData: {},
    originalFormData: {},
    errors: {},
  };

  componentDidMount(): void {
    this.props.actions.getBankAccount(
      this.props.currentUser.bankAccount.id,
    );
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed) {
      const formData = {
        bankName: nextProps.currentBankAccount.bankName || '',
        routingNumber: nextProps.currentBankAccount.routingNumber || '',
        accountNumber: nextProps.currentBankAccount.accountNumber || '',
        accountHolderName: nextProps.currentBankAccount.accountHolderName || '',
        country:
          nextProps.currentBankAccount && nextProps.currentBankAccount.country
            ? nextProps.currentBankAccount.country.id
            : 'AUS',
      };
      return {
        ...prevState,
        formData,
        originalFormData: formData,
        errors: nextProps.errors,
      };
    }
    return null;
  }

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: [],
      changed,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleSubmit = (e): void => {
    if (this.isValid()) {
      if (this.props.currentBankAccount.id == null) {
        this.props.actions.createBankAccount({
          bankName: this.state.formData.bankName,
          routingNumber: this.state.formData.routingNumber,
          accountNumber: this.state.formData.accountNumber,
          accountHolderName: this.state.formData.accountHolderName,
          user: {
            id: this.props.currentUserId,
          },
          country: {
            id: 'AUS',
          },
        });
      } else {
        this.props.actions.createBankAccount({
          bankName: this.state.formData.bankName,
          routingNumber: this.state.formData.routingNumber,
          accountNumber: this.state.formData.accountNumber,
          accountHolderName: this.state.formData.accountHolderName,
          user: {
            id: this.props.currentUserId,
          },
          country: {
            id: 'AUS',
          },
        });
      }
    }
    this.setState({ changed: false });
  };

  render(): React.ReactNode {
    return (
      <div>
        <div className="row">
          <div className="col">
            <HeaderThin
              breadcrumb="Settings"
              breadcrumbLink="/settings"
              title="Bank Payouts"
              divider
            />
          </div>
        </div>
        <div className="row">
          <div className="col col-12 col-lg-8 col-xl-6">
            <SectionGroup title="Bank Information">
              <div className="row">
                <div className="col">
                  <span className="form-description">
                    All payments received from team members will be sent to this bank account
                    daily. Payouts sent to your typically arrive in 1-3 business days.
                    {' '}
                    <a href="https://help.teammo.com.au/en/articles/3530012-how-long-does-it-take-to-receive-payments-from-team-members" target="_blank">Learn more</a>
                    .
                  </span>
                </div>
              </div>
              <div className="row with-margin-top">
                <div className="col">
                  <SchemaForm
                    schema={formSchema}
                    // uiSchema={uiSchema}
                    handleChange={this.handleChange}
                    handleError={this.handleError}
                    handleSubmit={this.handleSubmit}
                    formData={this.state.formData}
                    isValid={this.isValid()}
                    isLoading={this.props.isSaving}
                    hasSaved={this.props.hasSaved}
                    hasFailed={this.props.hasFailed}
                    originalFormData={this.state.originalFormData}
                  />
                </div>
              </div>
            </SectionGroup>
            <div className="row with-margin-top">
              <div className="col">
                <Link className="btn-2 btn-thin d-inline-flex align-content-center" to="/profile">
                  Edit personal information
                  {' '}
                  <Isvg className="right-arrow-icon" src={ArrowRightIcon} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
